import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaPenNib, FaCheck, FaTimes, FaLock, FaUserPlus, FaCalendarAlt, FaSearch } from 'react-icons/fa';

const API_BASE_URL = import.meta.env.VITE_NEW_WORDS_API_URL || 'http://localhost:8080';

export const SetterDashboardPage: React.FC = () => {
  const { user, token, login, register, logout } = useAuth();
  
  // Auth Tab: 'login' | 'register'
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Auth fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Setter Profile State
  const [setterProfile, setSetterProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [penName, setPenName] = useState('');
  const [applyError, setApplyError] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);

  // Drawing Canvas State
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [pathLength, setPathLength] = useState(0);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Dashboard Word Search & Submit State
  const [searchQuery, setSearchQuery] = useState('');
  const [foundWords, setFoundWords] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedWord, setSelectedWord] = useState<any>(null);
  const [note, setNote] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSuccess, setScheduleSuccess] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  // Load Setter Profile
  useEffect(() => {
    if (token) {
      fetchSetterProfile();
    } else {
      setSetterProfile(null);
      setProfileLoading(false);
    }
  }, [token]);

  const fetchSetterProfile = async () => {
    setProfileLoading(true);
    setApplyError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/setters/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSetterProfile(data.setter);
      } else {
        setSetterProfile(null);
      }
    } catch (err) {
      console.error('Error fetching setter profile:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  // Canvas Drawing Handlers
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return null;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Support mouse or touch
    let clientX, clientY;
    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;
    setIsDrawing(true);
    lastPointRef.current = coords;

    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#f8fafc'; // slate-50
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !lastPointRef.current || !canvasRef.current) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      // Calculate path distance added
      const dx = coords.x - lastPointRef.current.x;
      const dy = coords.y - lastPointRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      setPathLength(prev => prev + dist);

      lastPointRef.current = coords;
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPointRef.current = null;
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setPathLength(0);
    }
  };

  // Auth Actions
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authTab === 'login') {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
    } catch (err: any) {
      setAuthError(err.message || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // Apply Action
  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    setApplyError('');
    if (pathLength < 150) {
      setApplyError('Please write a fuller signature before submitting.');
      return;
    }
    if (!canvasRef.current) return;

    setApplyLoading(true);
    try {
      // Export signature canvas as transparent WebP
      const signatureImage = canvasRef.current.toDataURL('image/webp');
      const res = await fetch(`${API_BASE_URL}/api/setters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ penName, signatureImage })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit application');
      }

      setSetterProfile(data.setter);
    } catch (err: any) {
      setApplyError(err.message || 'Failed to submit application');
    } finally {
      setApplyLoading(false);
    }
  };

  // Word Search
  const searchWords = async (query: string) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setFoundWords([]);
      return;
    }

    setSearching(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/words/search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setFoundWords(data.words || []);
      }
    } catch (err) {
      console.error('Error searching words:', err);
    } finally {
      setSearching(false);
    }
  };

  // Schedule Word Action
  const handleScheduleWord = async (e: React.FormEvent) => {
    e.preventDefault();
    setScheduleError('');
    setScheduleSuccess('');
    if (!selectedWord || !note.trim() || !scheduleDate) {
      setScheduleError('All fields are required');
      return;
    }

    setSubmitLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/words/${selectedWord._id}/set-word-of-the-day`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note, date: scheduleDate })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to schedule word');
      }

      setScheduleSuccess(`Success! "${selectedWord.word.toUpperCase()}" is now scheduled for ${scheduleDate}.`);
      setSelectedWord(null);
      setNote('');
      setScheduleDate('');
    } catch (err: any) {
      setScheduleError(err.message || 'Failed to schedule word');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto">
        <header className="flex justify-between items-center mb-12 border-b border-zinc-800 pb-6">
          <div className="flex items-center gap-3">
            <FaPenNib className="text-violet-500 text-3xl" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-indigo-500">
                Zwizle Contributor Workspace
              </h1>
              <p className="text-sm text-zinc-400">Set the Word of the Day & share your story</p>
            </div>
          </div>
          {user && (
            <button
              onClick={logout}
              className="text-sm bg-zinc-900 hover:bg-zinc-800 text-zinc-300 py-1.5 px-4 rounded-full border border-zinc-800 hover:border-zinc-700 transition"
            >
              Sign Out
            </button>
          )}
        </header>

        {/* 1. AUTHENTICATION REQUIRED */}
        {!user && (
          <div className="max-w-md mx-auto bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl backdrop-blur-md">
            <div className="flex gap-4 mb-8 border-b border-zinc-900 pb-2">
              <button
                onClick={() => setAuthTab('login')}
                className={`flex-1 pb-3 text-center text-sm font-semibold transition ${
                  authTab === 'login' ? 'text-violet-400 border-b-2 border-violet-400' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setAuthTab('register')}
                className={`flex-1 pb-3 text-center text-sm font-semibold transition ${
                  authTab === 'register' ? 'text-violet-400 border-b-2 border-violet-400' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleAuth} className="space-y-5">
              {authTab === 'register' && (
                <div>
                  <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Pen Name / Full Name</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition"
                    placeholder="Jokonia"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition"
                  placeholder="name@domain.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition"
                  placeholder="••••••••"
                />
              </div>

              {authError && <div className="text-red-400 text-sm">{authError}</div>}

              <button
                type="submit"
                disabled={authLoading}
                className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition flex justify-center items-center gap-2"
              >
                {authLoading ? 'Loading...' : authTab === 'login' ? <><FaLock /> Sign In</> : <><FaUserPlus /> Register</>}
              </button>
            </form>
          </div>
        )}

        {/* 2. LOADING STATE */}
        {user && profileLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-violet-500 mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading profile...</p>
          </div>
        )}

        {/* 3. SETTER APPLICATION FORM (NO PROFILE YET) */}
        {user && !profileLoading && !setterProfile && (
          <div className="max-w-2xl mx-auto bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl">
            <h2 className="text-xl font-bold mb-2">Apply to become a Setter</h2>
            <p className="text-sm text-zinc-400 mb-6">
              Create a custom pen name and draw your signature on our canvas. Players will see this signature once they successfully guess your WOTD.
            </p>

            <form onSubmit={handleApply} className="space-y-6">
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Pen Name (Public Identity)</label>
                <input
                  type="text"
                  required
                  maxLength={40}
                  value={penName}
                  onChange={(e) => setPenName(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition"
                  placeholder="e.g. Jokonia, The Wordsmith"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1 flex justify-between">
                  <span>Sign your signature</span>
                  <span className="text-[10px] text-zinc-500">Min strokes required</span>
                </label>
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-2 relative">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={200}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="w-full h-40 bg-zinc-950 rounded-xl border border-zinc-900 cursor-crosshair touch-none"
                  />
                  
                  {/* Progress bar to track minimum stroke length */}
                  <div className="mt-2 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${pathLength >= 150 ? 'bg-green-500' : 'bg-violet-500'}`}
                      style={{ width: `${Math.min((pathLength / 150) * 100, 100)}%` }}
                    />
                  </div>

                  <div className="flex justify-between items-center mt-3 text-xs">
                    <span className={pathLength >= 150 ? 'text-green-400' : 'text-zinc-500'}>
                      {pathLength >= 150 ? 'Signature complete ✓' : 'Keep drawing...'}
                    </span>
                    <button
                      type="button"
                      onClick={clearCanvas}
                      className="text-violet-400 hover:text-violet-300 font-semibold cursor-pointer"
                    >
                      Clear & Retry
                    </button>
                  </div>
                </div>
              </div>

              {applyError && <div className="text-red-400 text-sm">{applyError}</div>}

              <button
                type="submit"
                disabled={applyLoading || pathLength < 150 || !penName.trim()}
                className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition flex justify-center items-center gap-2"
              >
                {applyLoading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        )}

        {/* 4. APPLICATION PENDING */}
        {user && !profileLoading && setterProfile && setterProfile.status === 'pending' && (
          <div className="max-w-md mx-auto text-center bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 to-yellow-400" />
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaPenNib className="text-amber-500 text-2xl animate-pulse" />
            </div>
            <h2 className="text-xl font-bold mb-2">Application Pending Approval</h2>
            <p className="text-sm text-zinc-400 mb-6">
              Thanks, <span className="text-white font-semibold">{setterProfile.penName}</span>! Our moderation team is currently reviewing your profile and signature.
            </p>
            <div className="bg-zinc-900 rounded-2xl p-4 flex flex-col items-center">
              <span className="text-xs uppercase tracking-wider text-zinc-500 block mb-2">Your Signature Snapshot</span>
              <img
                src={`${API_BASE_URL}${setterProfile.signatureUrl}`}
                alt="Signature Snapshot"
                className="h-16 object-contain invert opacity-80"
              />
            </div>
          </div>
        )}

        {/* 5. APPLICATION REJECTED */}
        {user && !profileLoading && setterProfile && setterProfile.status === 'rejected' && (
          <div className="max-w-md mx-auto text-center bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-red-500" />
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaTimes className="text-red-500 text-2xl" />
            </div>
            <h2 className="text-xl font-bold mb-2">Application Rejected</h2>
            <p className="text-sm text-zinc-400 mb-6">
              Unfortunately, your profile application was not approved. This could be due to inappropriate signature content or pen name.
            </p>
            <button
              onClick={() => {
                // Clear state to reapply
                setSetterProfile(null);
              }}
              className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-xl transition"
            >
              Reapply
            </button>
          </div>
        )}

        {/* 6. APPROVED SETTER DASHBOARD */}
        {user && !profileLoading && setterProfile && setterProfile.status === 'approved' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contributor Profile sidebar */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-3xl p-6 h-fit">
              <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-4">Your Contributor Card</h3>
              <div className="space-y-4">
                <div>
                  <span className="text-xs text-zinc-500">Pen Name</span>
                  <div className="text-lg font-bold text-violet-400">{setterProfile.penName}</div>
                </div>
                <div>
                  <span className="text-xs text-zinc-500 block mb-2">Your Signature</span>
                  <div className="bg-zinc-900/50 rounded-xl p-3 border border-zinc-900 flex justify-center items-center h-20">
                    <img
                      src={`${API_BASE_URL}${setterProfile.signatureUrl}`}
                      alt="Signature"
                      className="max-h-full object-contain invert"
                    />
                  </div>
                </div>
                <div className="text-xs text-zinc-500 border-t border-zinc-900 pt-4">
                  Profile Active Since: {new Date(setterProfile.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Set WOTD Column */}
            <div className="md:col-span-2 space-y-6">
              {scheduleSuccess && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-2xl p-4 flex items-center gap-3">
                  <FaCheck className="flex-shrink-0" />
                  <p className="text-sm">{scheduleSuccess}</p>
                </div>
              )}

              {scheduleError && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl p-4 flex items-center gap-3">
                  <FaTimes className="flex-shrink-0" />
                  <p className="text-sm">{scheduleError}</p>
                </div>
              )}

              <div className="bg-zinc-950 border border-zinc-900 rounded-3xl p-8">
                <h2 className="text-xl font-bold mb-2">Schedule a Word of the Day</h2>
                <p className="text-sm text-zinc-400 mb-6">
                  Select an available word, write a brief 280-character explanation on why it matters to you, and pick a date.
                </p>

                <form onSubmit={handleScheduleWord} className="space-y-6">
                  {/* Word Searcher */}
                  <div className="relative">
                    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Search target word</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={selectedWord ? selectedWord.word.toUpperCase() : searchQuery}
                        disabled={!!selectedWord}
                        onChange={(e) => searchWords(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-violet-500 disabled:opacity-50 transition"
                        placeholder="Search word..."
                      />
                      <FaSearch className="absolute left-3.5 top-4 text-zinc-500" />
                      {selectedWord && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWord(null);
                            setSearchQuery('');
                          }}
                          className="absolute right-3 top-3 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-1 px-2.5 rounded-lg transition"
                        >
                          Change
                        </button>
                      )}
                    </div>

                    {/* Dropdown search results */}
                    {!selectedWord && searchQuery.trim().length >= 2 && (
                      <div className="absolute left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl max-h-48 overflow-y-auto z-10">
                        {searching ? (
                          <div className="p-4 text-sm text-zinc-500">Searching...</div>
                        ) : foundWords.length === 0 ? (
                          <div className="p-4 text-sm text-zinc-500">No words found</div>
                        ) : (
                          foundWords.map((word) => (
                            <button
                              key={word._id}
                              type="button"
                              onClick={() => setSelectedWord(word)}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-800/50 transition border-b border-zinc-800/40 last:border-0 flex justify-between items-center"
                            >
                              <span className="font-semibold text-white">{word.word.toUpperCase()}</span>
                              <span className="text-xs text-zinc-500 lowercase">{word.partOfSpeech}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Date picker */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Reveal Date</label>
                    <div className="relative">
                      <input
                        type="date"
                        required
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-violet-500 transition"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <FaCalendarAlt className="absolute left-3.5 top-4 text-zinc-500" />
                    </div>
                  </div>

                  {/* Note input */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex justify-between">
                      <span>Story Note</span>
                      <span className={`${note.length > 280 ? 'text-red-400' : 'text-zinc-500'}`}>{note.length} / 280</span>
                    </label>
                    <textarea
                      required
                      maxLength={280}
                      rows={4}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition resize-none"
                      placeholder="Why does this word matter to you? Tell the players your story..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitLoading || !selectedWord || note.length > 280 || !note.trim() || !scheduleDate}
                    className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition flex justify-center items-center gap-2"
                  >
                    {submitLoading ? 'Scheduling...' : 'Lock in Word of the Day'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
